// Auction.jsx — Root. Wires poller → AuctionProvider → Layers + UI.
// Route: /auction

import { useEffect, useState } from 'react';
import useAuctionPoller from '../hooks/useAuctionPoller';
import { apiService } from '../services/api';
import useStore from '../store/store';
import { AuctionProvider, useAuction } from './AuctionContext';
import ParticleCanvas from './ParticleCanvas';
import HeroCard from './HeroCard';
import './tokens.css';
import './layers.css';
import './HeroCard.css';
import './ui.css';

const ROLE_COLORS = {
  duelist: '#FF4655', initiator: '#32F5D2',
  controller: '#A78BFA', sentinel: '#FACC15', flex: '#94A3B8',
};
const TIER_COLORS = {
  platinum: '#9BE8E0', diamond: '#F472B6',
  ascendant: '#3FE07F', immortal: '#FF5277', radiant: '#FFD84D',
};
const tc = (t) => TIER_COLORS[(t||'').toLowerCase()] || '#32F5D2';

const AUCTION_PASSWORD = 'vplseason5owners';
const SESSION_KEY      = 'vpl_auction_auth';

// Cache team logos once — never re-fetch during auction
const teamLogoCache = {};

function PasswordGate({ onUnlock }) {
  const [input, setInput] = useState('');
  const [error, setError] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (input === AUCTION_PASSWORD) {
      sessionStorage.setItem(SESSION_KEY, '1');
      onUnlock();
    } else {
      setError(true);
      setInput('');
      setTimeout(() => setError(false), 1500);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', background: '#06070B',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Devator', sans-serif",
    }}>
      <div style={{
        background: '#11131A', border: '1px solid rgba(50,245,210,0.2)',
        borderRadius: '16px', padding: '48px', textAlign: 'center', width: '340px',
      }}>
        <div style={{ fontSize: '28px', fontWeight: 900, color: '#32F5D2', letterSpacing: '6px', marginBottom: '8px' }}>
          VPL
        </div>
        <h2 style={{ color: '#f1f5f9', fontSize: '18px', margin: '0 0 4px', fontWeight: 700 }}>
          Season 5 Auction
        </h2>
        <p style={{ color: '#475569', fontSize: '12px', marginBottom: '28px', letterSpacing: '1px' }}>
          Owner access only
        </p>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <input
            type="password"
            placeholder="Enter password"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            autoFocus
            style={{
              background: '#06070B',
              border: `1px solid ${error ? '#ef4444' : 'rgba(255,255,255,0.1)'}`,
              borderRadius: '8px', color: '#e2e8f0',
              fontSize: '15px', padding: '12px 16px',
              outline: 'none', textAlign: 'center',
              letterSpacing: '2px', fontFamily: "'Devator', sans-serif",
            }}
          />
          {error && (
            <p style={{ color: '#ef4444', fontSize: '11px', margin: '-4px 0', letterSpacing: '1px' }}>
              Incorrect password
            </p>
          )}
          <button
            type="submit"
            style={{
              background: '#32F5D2', color: '#06070B', border: 'none',
              borderRadius: '8px', fontSize: '14px', fontWeight: 900,
              padding: '12px', cursor: 'pointer', letterSpacing: '2px',
              textTransform: 'uppercase', fontFamily: "'Devator', sans-serif",
            }}
          >
            ENTER
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Auction() {
  const seasonId = useStore(state => state.currentSeason?.id);
  const [authed, setAuthed] = useState(
    () => sessionStorage.getItem(SESSION_KEY) === '1'
  );

  useEffect(() => {
    const st = useStore.getState();
    if (!st.currentSeason && !st.loading?.seasons) st.fetchSeasons();
  }, []);

  // Load team logos once — stored in module-level cache, never re-fetched
  useEffect(() => {
    if (!seasonId) return;
    apiService.getSeasonTeams(seasonId).then(res => {
      const teams = res.data || [];
      teams.forEach(t => {
        if (t.team_id && t.logo) teamLogoCache[t.team_id] = t.logo;
        if (t.id && t.logo) teamLogoCache[t.id] = t.logo;
      });
    }).catch(() => {});
  }, [seasonId]);

  const { state, loading, error } = useAuctionPoller(seasonId, authed);

  if (!authed) return <PasswordGate onUnlock={() => setAuthed(true)} />;

  if (loading) return (
    <div className="au-root au-loading-screen">
      <div className="au-spinner" />
      <p>Connecting to live auction…</p>
    </div>
  );

  if (error || !state?.status) return (
    <div className="au-root au-offline-screen">
      <div className="au-offline-icon">⚡</div>
      <h2>Auction Offline</h2>
      <p>{error || 'No active session.'}</p>
    </div>
  );

  if (state?.status === 'completed') return (
    <div className="au-root au-offline-screen">
      <div className="au-offline-icon">✓</div>
      <h2>Auction Complete</h2>
      <p>Season 5 auction has ended.</p>
      <a href="/auction/results" style={{
        marginTop: '16px', display: 'inline-block',
        padding: '10px 24px', background: 'transparent',
        border: '1px solid rgba(50,245,210,0.4)', borderRadius: '6px',
        color: '#32F5D2', fontSize: '12px', fontWeight: 700,
        letterSpacing: '2px', textTransform: 'uppercase', textDecoration: 'none',
      }}>VIEW RESULTS →</a>
    </div>
  );

  return (
    <AuctionProvider auctionState={state} teamLogoCache={teamLogoCache}>
      <AuctionLayout />
    </AuctionProvider>
  );
}

// Sold color wash — tints stage with player tier color for 300ms
function getSoldHue(tier) {
  const hues = {
    immortal: '340deg', diamond: '310deg',
    ascendant: '150deg', platinum: '180deg', radiant: '45deg',
  };
  return hues[(tier||'').toLowerCase()] || '180deg';
}

/* ── Layout — assembles all layers ── */
function AuctionLayout() {
  const { stage, player } = useAuction();
  const tier = player ? tc(player.rank_tier) : '#32F5D2';

  return (
    <div className="au-root">
      {/* z0 — Arena Background */}
      <div className="au-arena"
        style={{ backgroundImage: "url('/images/background-auction.webp')" }}
        aria-hidden="true"
      />

      {/* z1 — Dark Overlay */}
      <div className="au-overlay" aria-hidden="true" />

      {/* z2 — Spotlights */}
      <div className="au-spotlights" data-stage={stage} aria-hidden="true">
        <div className="au-spotlight au-spotlight-l" />
        <div className="au-spotlight au-spotlight-c" />
        <div className="au-spotlight au-spotlight-r" />
      </div>

      {/* z3 — Particles */}
      <ParticleCanvas />

      {/* z4 — Fog */}
      <div className="au-fog" aria-hidden="true">
        <div className="au-fog-a" />
        <div className="au-fog-b" />
      </div>

      {/* z5 — Auction Stage */}
      <div className="au-stage" data-stage={stage} aria-hidden="true"
        style={stage === 'sold' && player ? { '--sold-hue': getSoldHue(player.rank_tier) } : {}}>
        <div className="au-platform">
          {/* Platform PNG */}
          <img src="/images/platform.webp" alt="" />
        </div>
        <div className="au-stage-glow"
          style={{ background: `radial-gradient(ellipse at center, ${tier}28 0%, transparent 65%)` }}
        />
        <div className="au-stage-reflection" />
      </div>

      {/* z6 — Hero Card */}
      <div className="au-herocard-wrap" data-stage={stage}>
        <HeroCard />
      </div>
      {/* Card reflection on platform */}
      {stage !== 'standby' && <div className="au-card-reflection" />}

      {/* z7 — UI */}
      <div className="au-ui">
        <TopBar />
        <main className="au-main">
          <TeamPanel />
          <CenterPanel />
          <RightPanel />
        </main>
        <StatusBar />
      </div>
    </div>
  );
}

/* ── TopBar ── */
function TopBar() {
  const { sets, currentSetNum, sessionStatus } = useAuction();
  return (
    <header className="au-topbar">
      <div className="au-logo-wrap">
        <span className="au-logo">VPL</span>
        <span className="au-logo-sub">SEASON 5</span>
      </div>
      <div className="au-topbar-center">
        <div className="au-live-wrap">
          <span className="au-live-dot" />
          <span className="au-live-text">LIVE AUCTION</span>
          <span className="au-on-air">● ON AIR</span>
        </div>
        <div className="au-rounds-wrap">
          <div className="au-rounds-label">
            ROUND {String(currentSetNum).padStart(2,'0')} / {String(sets.length).padStart(2,'0')}
          </div>
          <div className="au-rounds">
            {sets.map(s => (
              <div key={s.set_number}
                className={`au-round-chip ${s.set_number === currentSetNum ? 'au-rc-active' : ''} ${s.status === 'completed' ? 'au-rc-done' : ''}`}>
                {s.set_number}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="au-topbar-right" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <StatusPill status={sessionStatus} />
      </div>
    </header>
  );
}

/* ── Team Panel ── */
function TeamPanel() {
  const { teamBudgets } = useAuction();
  return (
    <aside className="au-left">
      <div className="au-panel-label">TEAM PURSE</div>
      <div className="au-teams">
        {teamBudgets.map((tb, i) => <TeamCard key={tb.team_id} tb={tb} rank={i+1} />)}
      </div>
    </aside>
  );
}

function TeamCard({ tb, rank }) {
  const spent    = (tb.starting_budget||0) - (tb.available_budget||0);
  const pct      = tb.starting_budget > 0 ? Math.min(100, Math.round((spent/tb.starting_budget)*100)) : 0;
  const barColor = pct > 75 ? '#FF4655' : pct > 50 ? '#FACC15' : '#32F5D2';
  const rankColors = ['#FFD84D','#C0C0C0','#CD7F32'];
  return (
    <div className="au-team">
      <div className="au-team-rank" style={{ color: rankColors[rank-1]||'#334155' }}>
        {String(rank).padStart(2,'0')}
      </div>
      <div className="au-team-logo">
        {tb.team_logo ? <img src={tb.team_logo} alt="" /> : <span>{(tb.team_name||'?')[0]}</span>}
      </div>
      <div className="au-team-mid">
        <div className="au-team-name">{tb.team_name}</div>
        <div className="au-team-bar-bg">
          <div className="au-team-bar" style={{ width:`${pct}%`, background:barColor }} />
        </div>
        <div className="au-team-meta">{tb.players_bought} PLAYERS · {spent} PTS SPENT</div>
      </div>
      <div className="au-team-right">
        <div className="au-team-pts" style={{ color: pct>75?'#FF4655':'#32F5D2' }}>
          {tb.available_budget}
        </div>
        <div className="au-team-pts-label">PTS</div>
      </div>
    </div>
  );
}

/* ── Center Panel ── */
function CenterPanel() {
  const { player, totalSold, totalPlayers, currentSetNum, sets } = useAuction();
  return (
    <section className="au-center">
      <div className="au-now-label">
        <span className="au-now-arrow">◄◄</span>
        NOW ON THE BLOCK
        <span className="au-now-arrow">►►</span>
      </div>
      {!player && (
        <div className="au-waiting">
          <div className="au-wait-rings">
            {[1,2,3].map(s => <div key={s} className="au-wait-ring" style={{ '--s': s }} />)}
          </div>
          <p>Waiting for next player…</p>
        </div>
      )}
      {/* Spacer so panels fill height */}
      <div style={{ flex: 1 }} />
      {player && (
        <div className="au-bid-display">
          <div className="au-bid-waveform" aria-hidden="true">
            {[...Array(24)].map((_,i) => <div key={i} className="au-wave-bar" style={{ '--i': i }} />)}
          </div>
          <div className="au-bid-center">
            <div className="au-bid-label-top">CURRENT PLAYER</div>
            <div className="au-bid-big">
              {String(totalSold+1).padStart(2,'0')}
              <span> / {String(totalPlayers).padStart(3,'0')}</span>
            </div>
            <div className="au-bid-min">SET {currentSetNum} OF {sets.length}</div>
          </div>
          <div className="au-bid-waveform au-bid-waveform-r" aria-hidden="true">
            {[...Array(24)].map((_,i) => <div key={i} className="au-wave-bar" style={{ '--i': 23-i }} />)}
          </div>
        </div>
      )}
    </section>
  );
}

/* ── Right Panel ── */
function RightPanel() {
  const { recentSales, player, bidHistory } = useAuction();
  const tier = player ? tc(player.rank_tier) : '#32F5D2';
  return (
    <aside className="au-right">
      <div className="au-panel-label">RECENT SALES</div>
      <div className="au-sales">
        {recentSales.length === 0 && <p className="au-empty">No sales yet</p>}
        {recentSales.slice(0,6).map((sale,i) => (
          <SaleRow key={i} sale={sale} age={i} />
        ))}
      </div>
      {player && (
        <>
          <div className="au-panel-label" style={{ marginTop: 14 }}>BID HISTORY</div>
          <div className="au-bids">
            {bidHistory.map((row,i) => (
              <div key={i}
                className={`au-bid-row ${i===0 && row.time==='LIVE' ? 'au-bid-active' : ''}`}
                style={{ opacity: Math.max(0.25, 1 - i*0.18) }}
              >
                <div className="au-bid-dot"
                  style={{ background: i===0 && row.time==='LIVE' ? tier : '#1e293b' }} />
                <span className="au-bid-pts"
                  style={{ color: i===0 && row.time==='LIVE' ? tier : '#475569' }}>
                  {row.pts} PTS
                </span>
                <span className="au-bid-team">{row.label}</span>
                <span className="au-bid-time">{row.time}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </aside>
  );
}

function SaleRow({ sale, age }) {
  const unsold = sale.action === 'unsold';
  const color  = ROLE_COLORS[(sale.role||'').toLowerCase()] || '#94A3B8';
  return (
    <div className="au-sale" style={{ opacity: Math.max(0.4, 1 - age*0.12) }}>
      <div className={`au-sale-badge ${unsold ? 'au-badge-unsold' : 'au-badge-sold'}`}>
        {unsold ? 'UNSOLD' : 'SOLD'}
      </div>
      <div className="au-sale-initial" style={{ borderColor: color, color }}>
        {(sale.player_name||'?')[0].toUpperCase()}
      </div>
      <div className="au-sale-info">
        <div className="au-sale-name">{sale.player_name}</div>
        <div className="au-sale-role" style={{ color }}>{sale.role||'—'}</div>
      </div>
      {!unsold && (
        <div className="au-sale-right">
          <div className="au-sale-pts">{sale.amount} PTS</div>
          <div className="au-sale-team">To {sale.team_name}</div>
        </div>
      )}
    </div>
  );
}


/* ── Bid History Panel ── */
// function BidHistoryPanel({ bid, basePrice, tier, playerKey }) {
//   const [history, setHistory] = useState([
//     { pts: basePrice, label: 'Start Price', time: '00:00' }
//   ]);
//   const prevBid = useRef(0);
//   const prevKey = useRef(playerKey);

//   useEffect(() => {
//     if (playerKey !== prevKey.current) {
//       prevKey.current = playerKey;
//       setHistory([{ pts: basePrice, label: 'Start Price', time: '00:00' }]);
//       prevBid.current = 0;
//     }
//   }, [playerKey, basePrice]);

//   useEffect(() => {
//     if (bid > 0 && bid !== prevBid.current) {
//       prevBid.current = bid;
//       setHistory(prev => [
//         { pts: bid, label: 'Current Bid', time: 'LIVE' },
//         ...prev
//       ].slice(0, 5));
//     }
//   }, [bid]);

//   const current = history[0]?.time === 'LIVE' ? history[0] : null;
//   const rest = current ? history.slice(1) : history;

//   return (
//     <div className="au-bids">
//       {current && (
//         <div className="au-bid-row au-bid-active">
//           <div className="au-bid-dot" style={{ background: tier }} />
//           <span className="au-bid-pts" style={{ color: tier, textShadow: `0 0 12px ${tier}55` }}>
//             {current.pts} PTS
//           </span>
//           <span className="au-bid-team">{current.label}</span>
//           <span className="au-bid-time">{current.time}</span>
//         </div>
//       )}
//       {history.length > 1 && <div className="au-bid-divider" />}
//       {rest.map((row, i) => (
//         <div key={i} className="au-bid-row" style={{ opacity: Math.max(0.3, 1 - i * 0.2) }}>
//           <div className="au-bid-dot" style={{ background: '#1e293b' }} />
//           <span className="au-bid-pts" style={{ color: '#475569' }}>{row.pts} PTS</span>
//           <span className="au-bid-team">{row.label}</span>
//           <span className="au-bid-time">{row.time}</span>
//         </div>
//       ))}
//     </div>
//   );
// }

/* ── Status Bar ── */
function StatusBar() {
  const { sessionStatus, currentSetNum, sets, totalSold, totalUnsold, totalPlayers, remaining } = useAuction();
  return (
    <footer className="au-footer">
      <FStat icon="●" label="AUCTION STATUS" value={(sessionStatus || 'pending').toUpperCase()} green />
      <div className="au-footer-sep" />
      <FStat icon="◎" label="CURRENT ROUND" value={`${String(currentSetNum).padStart(2,'0')} / ${String(sets.length).padStart(2,'0')}`} />
      <div className="au-footer-sep" />
      <FStat icon="👤" label="PLAYERS SOLD" value={`${totalSold} / ${totalPlayers}`} />
      <div className="au-footer-sep" />
      <FStat icon="⏳" label="REMAINING" value={`${Math.max(0, remaining)} PLAYERS`} />
      <div className="au-footer-sep" />
      <FStat icon="✕" label="UNSOLD" value={`${totalUnsold} PLAYERS`} />
    </footer>
  );
}

function FStat({ icon, label, value, green }) {
  return (
    <div className="au-fstat">
      <span className="au-fstat-icon" style={{ color: green ? '#4ADE80' : '#475569' }}>{icon}</span>
      <div>
        <div className="au-fstat-label">{label}</div>
        <div className="au-fstat-val" style={{ color: green ? '#4ADE80' : '#32F5D2' }}>{value}</div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending:      { text: 'STANDBY',    cls: 'au-pill-standby' },
    active:       { text: '● LIVE',     cls: 'au-pill-live' },
    unsold_round: { text: '⟳ UNSOLD',   cls: 'au-pill-unsold' },
    completed:    { text: '✓ COMPLETE', cls: 'au-pill-done' },
  };
  const { text, cls } = map[status] || { text: status, cls: 'au-pill-standby' };
  return <span className={`au-pill ${cls}`}>{text}</span>;
}